import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, addMinutes } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { validateNIF, validatePhone } from '../utils/ptValidators';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Trash2, UserPlus, Clock, MapPin, Phone, Video, Calendar as CalendarIcon, Loader2, Trophy, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_OPTIONS = [
  { value: 'evento', label: 'Evento', icon: CalendarIcon, color: 'text-sky-500' },
  { value: 'reuniao', label: 'Reunião', icon: Video, color: 'text-violet-500' },
  { value: 'chamada', label: 'Chamada Telefónica', icon: Phone, color: 'text-emerald-500' },
];

const STATUS_OPTIONS = [
  { value: 'agendado', label: 'Agendado' },
  { value: 'realizado', label: 'Realizado' },
  { value: 'cancelado', label: 'Cancelado' },
];

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Não repetir' },
  { value: 'daily', label: 'Diariamente' },
  { value: 'weekly', label: 'Semanalmente' },
  { value: 'monthly', label: 'Mensalmente' },
];

const emptyEvent = (defaultDate) => ({
  title: '',
  type: 'reuniao',
  scheduled_at: defaultDate || format(addMinutes(new Date(), 60), "yyyy-MM-dd'T'HH:mm"),
  duration_min: 30,
  location: '',
  description: '',
  client_name: '',
  client_phone: '',
  client_email: '',
  client_nif: '',
  assigned_to: '',
  attendee_ids: [],
  status: 'agendado',
  outcome_notes: '',
  recurrence: 'none',
  recurrence_end_at: '',
  lead_id: null,
});

export const CalendarEventDialog = ({ open, onOpenChange, event = null, defaultDate = null, onSaved }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!event?.id;
  const [form, setForm] = useState(emptyEvent(defaultDate));
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [users, setUsers] = useState([]);
  const [phoneErr, setPhoneErr] = useState('');
  const [nifErr, setNifErr] = useState('');

  const canEdit = isEdit
    ? (
        ['super_admin', 'gestor'].includes(user?.role)
        || event?.created_by === user?.id
        || event?.assigned_to === user?.id
        || (event?.attendee_ids || []).includes(user?.id)
      )
    : true;
  const isConverted = !!event?.lead_id;
  const linkedLeadId = event?.lead_id || null;

  useEffect(() => {
    if (open) {
      if (event) {
        setForm({
          ...emptyEvent(),
          ...event,
          attendee_ids: event.attendee_ids || [],
        });
      } else {
        setForm(emptyEvent(defaultDate));
      }
      setPhoneErr('');
      setNifErr('');
    }
  }, [open, event, defaultDate]);

  useEffect(() => {
    if (!open) return;
    // Fetch users for assigned_to + attendees (excluding angariadores)
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/users`);
        const filtered = (r.data || []).filter(u => u.role !== 'angariador' && u.active !== false);
        setUsers(filtered);
      } catch { /* ignore */ }
    })();
  }, [open]);

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validate = useCallback(() => {
    let ok = true;
    if (form.client_phone && !validatePhone(form.client_phone, 'telemovel').ok) {
      setPhoneErr('Telemóvel inválido (9 dígitos, começa por 9)');
      ok = false;
    } else setPhoneErr('');
    if (form.client_nif && !validateNIF(form.client_nif).ok) {
      setNifErr('NIF inválido');
      ok = false;
    } else setNifErr('');
    return ok;
  }, [form.client_phone, form.client_nif]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Título obrigatório'); return; }
    if (!form.scheduled_at) { toast.error('Data e hora obrigatórias'); return; }
    if (!validate()) { toast.error('Corrige os campos inválidos'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        type: form.type,
        scheduled_at: form.scheduled_at,
        duration_min: form.duration_min,
        location: form.location.trim(),
        description: form.description.trim(),
        client_name: form.client_name.trim(),
        client_phone: form.client_phone.trim(),
        client_email: form.client_email.trim(),
        client_nif: form.client_nif.trim(),
        assigned_to: form.assigned_to || user?.id,
        attendee_ids: form.attendee_ids || [],
        recurrence: form.recurrence,
        recurrence_end_at: form.recurrence_end_at || null,
      };
      if (isEdit) {
        payload.status = form.status;
        payload.outcome_notes = form.outcome_notes;
        await axios.put(`${API_URL}/calendar/event/${event.id}`, payload);
        toast.success('Evento atualizado');
      } else {
        await axios.post(`${API_URL}/calendar/event`, payload);
        toast.success('Evento criado');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm('Eliminar este evento? Esta ação não pode ser desfeita.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/calendar/event/${event.id}`);
      toast.success('Evento eliminado');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToLead = async () => {
    if (!isEdit) { toast.error('Guarda o evento primeiro'); return; }
    if (!form.client_name.trim() || !form.client_phone.trim()) {
      toast.error('Preenche nome e telefone do cliente antes de converter');
      return;
    }
    if (!validate()) return;
    setConverting(true);
    try {
      const r = await axios.post(`${API_URL}/calendar/event/${event.id}/convert-lead`, {
        source: 'outro',
        notes: `Convertido a partir do evento: ${form.title}`,
      });
      toast.success('Lead criada com sucesso 🎉');
      onOpenChange(false);
      onSaved?.();
      navigate(`/leads/${r.data.lead_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a converter');
    } finally {
      setConverting(false);
    }
  };

  const handleSnoozeMinutes = async (mins) => {
    if (!isEdit) return;
    try {
      const current = parseISO(form.scheduled_at);
      const next = format(addMinutes(current, mins), "yyyy-MM-dd'T'HH:mm");
      await axios.post(`${API_URL}/calendar/event/${event.id}/snooze`, { scheduled_at: next });
      setField('scheduled_at', next);
      toast.success(`Reagendado +${mins} min`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a reagendar');
    }
  };

  const toggleAttendee = (uid) => {
    setForm(prev => {
      const has = prev.attendee_ids.includes(uid);
      return { ...prev, attendee_ids: has ? prev.attendee_ids.filter(a => a !== uid) : [...prev.attendee_ids, uid] };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="calendar-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#2D3E6F]" />
            {isEdit ? 'Detalhes do Evento' : 'Novo Evento'}
            {linkedLeadId && (
              <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full ml-2">
                LIGADO A LEAD
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Vê, edita ou converte este evento em Lead.' : 'Agenda um evento, reunião ou chamada — recebes lembrete 15 min antes.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Type + Title */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Tipo *</Label>
              <Select value={form.type} onValueChange={(v) => setField('type', v)} disabled={!canEdit}>
                <SelectTrigger className="mt-1" data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <t.icon className={`w-3.5 h-3.5 ${t.color}`} />
                        {t.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Título *</Label>
              <Input
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder="Ex: Reunião com João Silva"
                className="mt-1"
                disabled={!canEdit}
                data-testid="event-title-input"
              />
            </div>
          </div>

          {/* Date + Duration */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Data e Hora *</Label>
              <Input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setField('scheduled_at', e.target.value)}
                className="mt-1"
                disabled={!canEdit}
                data-testid="event-datetime-input"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Duração (min)</Label>
              <Input
                type="number"
                min="5"
                max="1440"
                value={form.duration_min}
                onChange={(e) => setField('duration_min', parseInt(e.target.value) || 30)}
                className="mt-1"
                disabled={!canEdit}
                data-testid="event-duration-input"
              />
            </div>
          </div>

          {/* Snooze quick actions */}
          {isEdit && canEdit && form.status === 'agendado' && (
            <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100">
              <span className="text-xs text-amber-800 font-medium">Reagendar:</span>
              {[15, 30, 60, 1440].map(m => (
                <Button key={m} type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleSnoozeMinutes(m)} data-testid={`snooze-${m}`}>
                  +{m >= 1440 ? '1 dia' : `${m} min`}
                </Button>
              ))}
            </div>
          )}

          {/* Location */}
          <div>
            <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Local / Link (opcional)</Label>
            <Input
              value={form.location}
              onChange={(e) => setField('location', e.target.value)}
              placeholder="Ex: Escritório Coimbra, Zoom URL, telefone do cliente..."
              className="mt-1"
              disabled={!canEdit}
              data-testid="event-location-input"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs">Descrição (opcional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="Notas, agenda, tópicos a tratar..."
              className="mt-1 min-h-[60px]"
              disabled={!canEdit}
              data-testid="event-description-input"
            />
          </div>

          {/* Client section */}
          <div className="border-t pt-3">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Dados do Cliente (para converter em Lead)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome</Label>
                <Input value={form.client_name} onChange={(e) => setField('client_name', e.target.value)} className="mt-1" disabled={!canEdit} data-testid="event-client-name-input" />
              </div>
              <div>
                <Label className="text-xs">Telemóvel</Label>
                <Input value={form.client_phone} onChange={(e) => setField('client_phone', e.target.value)} className="mt-1" disabled={!canEdit} data-testid="event-client-phone-input" />
                {phoneErr && <p className="text-xs text-rose-500 mt-1">{phoneErr}</p>}
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.client_email} onChange={(e) => setField('client_email', e.target.value)} className="mt-1" disabled={!canEdit} data-testid="event-client-email-input" />
              </div>
              <div>
                <Label className="text-xs">NIF</Label>
                <Input value={form.client_nif} onChange={(e) => setField('client_nif', e.target.value)} className="mt-1" disabled={!canEdit} data-testid="event-client-nif-input" />
                {nifErr && <p className="text-xs text-rose-500 mt-1">{nifErr}</p>}
              </div>
            </div>
          </div>

          {/* Assigned + Attendees */}
          <div className="border-t pt-3">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-indigo-500" /> Atribuição & Convidados
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Atribuído a</Label>
                <Select value={form.assigned_to || user?.id} onValueChange={(v) => setField('assigned_to', v)} disabled={!canEdit}>
                  <SelectTrigger className="mt-1" data-testid="event-assigned-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Repetição</Label>
                <Select value={form.recurrence} onValueChange={(v) => setField('recurrence', v)} disabled={!canEdit}>
                  <SelectTrigger className="mt-1" data-testid="event-recurrence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Attendee chips */}
            <div className="mt-3">
              <Label className="text-xs">Convidados internos</Label>
              <div className="mt-1 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-lg border border-slate-200 bg-slate-50">
                {users.length === 0 && <p className="text-xs text-slate-400">A carregar utilizadores...</p>}
                {users.map(u => {
                  const isSelected = form.attendee_ids.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => canEdit && toggleAttendee(u.id)}
                      className={`text-xs px-2 py-1 rounded-full transition-colors ${isSelected ? 'bg-[#2D3E6F] text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
                      disabled={!canEdit}
                      data-testid={`attendee-${u.id}`}
                    >
                      {u.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Status + outcome (edit only) */}
          {isEdit && (
            <div className="border-t pt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setField('status', v)} disabled={!canEdit}>
                  <SelectTrigger className="mt-1" data-testid="event-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Notas / Resultado</Label>
                <Textarea
                  value={form.outcome_notes}
                  onChange={(e) => setField('outcome_notes', e.target.value)}
                  className="mt-1 min-h-[60px]"
                  placeholder="Resumo da reunião, resultado da chamada..."
                  disabled={!canEdit}
                  data-testid="event-outcome-input"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row gap-2 justify-between sm:justify-between flex-wrap">
          <div className="flex gap-2">
            {isEdit && canEdit && (
              <Button type="button" variant="outline" onClick={handleDelete} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 border-rose-200" data-testid="event-delete-button">
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
              </Button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {linkedLeadId && (
              <Button
                type="button"
                variant="outline"
                onClick={() => { onOpenChange(false); navigate(`/leads/${linkedLeadId}`); }}
                className="text-[#2D3E6F] border-[#2D3E6F]/30 hover:bg-[#2D3E6F]/5"
                data-testid="event-goto-lead-button"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Ir para Lead
              </Button>
            )}
            {isEdit && !isConverted && !linkedLeadId && (
              <Button
                type="button"
                onClick={handleConvertToLead}
                disabled={converting || !form.client_name.trim() || !form.client_phone.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="event-convert-lead-button"
              >
                {converting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Converter em Lead
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
            {canEdit && (
              <Button type="button" onClick={handleSave} disabled={saving} className="bg-[#2D3E6F] hover:bg-[#1a2a4a]" data-testid="event-save-button">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                {isEdit ? 'Guardar' : 'Criar Evento'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarEventDialog;
